import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Stack,
  Box,
  InputAdornment,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Paper,
} from '@mui/material';
import { AppConfigs } from '../SetUp/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

//need to generate IDs for GA
let nextId = 0;

function SearchAppsAutocomplete() {
  //IDs for GA
  const [instanceId] = useState(() => `search-apps-${++nextId}`);
  const statusId = `${instanceId}-status`;
  const instructionsId = `${instanceId}-instructions`;
  const resultsId = `${instanceId}-results`;

  const [searchText, setSearchText] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [statusMessage, setStatusMessage] = useState(''); //for SR
  const [isActive, setIsActive] = useState(false);
  const announceTimerRef = useRef(null); //for SR
  const inputRef = useRef(null);

  //used to deal with VO
  const isTouchLayout =
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

  //function component now
  const allApps = useMemo(
    () =>
      AppConfigs.Apps.filter((app) => !app.isDeleted)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((app) => ({ ...app, label: app.name })),
    [],
  );

  // need to clear the timer when the component unmounts, otherwise it will try to set state on an unmounted component
  useEffect(() => {
    return () => clearTimeout(announceTimerRef.current);
  }, []);

  //function componetn now
  const filteredOptions = useMemo(() => {
    if (!searchText) return [];
    const query = searchText.toLowerCase();
    return allApps.filter((opt) => `${opt.name} ${opt.desc}`.toLowerCase().includes(query));
  }, [searchText, allApps]);

  const filterOptions = useCallback((options, state) => {
    const query = state.inputValue.toLowerCase();
    return options.filter((opt) => `${opt.name} ${opt.desc}`.toLowerCase().includes(query));
  }, []);

  //added for screen reader
  const announceResults = useCallback(
    (count) => {
      clearTimeout(announceTimerRef.current);
      announceTimerRef.current = setTimeout(() => {
        if (count === 0) {
          setStatusMessage('No applications found.');
        } else {
          setStatusMessage(
            `${count} application${count !== 1 ? 's' : ''} found. ${
              isTouchLayout
                ? 'Tap Search on the keyboard to browse results.'
                : 'Use arrow keys to browse.'
            }`,
          );
        }
      }, 400);
    },
    [isTouchLayout],
  );

  const getOptionLabel = useCallback(
    (option) => (typeof option === 'string' ? option : option?.name || ''),
    [],
  );

  //updated from onBlur to onInputChange to fix issue with VO iOS
  // on iOS not reading results when the input is cleared
  const handleInputChange = useCallback(
    (e, text, reason) => {
      setSearchText(text);
      if ((reason === 'input' || reason === 'clear') && selectedApp && text !== selectedApp.name) {
        setSelectedApp(null);
      }
      if (text) {
        const count = allApps.filter((opt) =>
          `${opt.name} ${opt.desc}`.toLowerCase().includes(text.toLowerCase()),
        ).length;
        announceResults(count);
      } else {
        clearTimeout(announceTimerRef.current);
        setStatusMessage('');
      }
    },
    [selectedApp, allApps, announceResults],
  );

  //added to deal with VO iOS
  const handleMobileInputChange = useCallback(
    (e) => {
      const text = e.target.value;
      setSearchText(text);
      if (selectedApp) {
        setSelectedApp(null);
      }
      if (text) {
        const count = allApps.filter((opt) =>
          `${opt.name} ${opt.desc}`.toLowerCase().includes(text.toLowerCase()),
        ).length;
        announceResults(count);
      } else {
        clearTimeout(announceTimerRef.current);
        setStatusMessage('');
      }
    },
    [selectedApp, allApps, announceResults],
  );

  //desktop
  //set the search text = app name and set the selected app
  //handles null for desktop, not needed for mobile
  const handleAppChange = useCallback((e, app) => {
    setSearchText(app?.name || '');
    setSelectedApp(app || null);
    if (app) {
      setStatusMessage(`${app.name} selected.`);
    }
  }, []);

  //mobile
  const handleMobileSelect = useCallback((app) => {
    setSearchText(app.name);
    setSelectedApp(app);
    setStatusMessage(`${app.name} selected.`);
    inputRef.current?.focus();
  }, []);

  const onGoToApp = useCallback(() => {
    if (selectedApp?.link) {
      window.open(selectedApp.link, '_blank', 'noopener,noreferrer');
    }
  }, [selectedApp]);

  //for VO iOS if the user taps done on the kb, move focus to the first result instead of clsoe
  const focusFirstResult = useCallback(() => {
    const firstOption = document.getElementById(resultsId)?.querySelector('[role="option"]');
    if (firstOption) {
      firstOption.focus();
    }
  }, [resultsId]);

  const handleWrapperFocus = useCallback(() => {
    setIsActive(true);
  }, []);

  const handleWrapperBlur = useCallback(
    (event) => {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return;
      }

      if (
        isTouchLayout &&
        event.relatedTarget === null &&
        searchText &&
        !selectedApp &&
        filteredOptions.length > 0
      ) {
        setTimeout(() => focusFirstResult(), 0);
        return;
      }

      setIsActive(false);
    },
    [isTouchLayout, searchText, selectedApp, filteredOptions.length, focusFirstResult],
  );

  const handleMobileInputKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        focusFirstResult();
      }
    },
    [focusFirstResult],
  );

  const handleOptionKeyDown = useCallback(
    (e, app) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (e.target.nextElementSibling) {
            e.target.nextElementSibling.focus();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (e.target.previousElementSibling) {
            e.target.previousElementSibling.focus();
          } else {
            inputRef.current?.focus();
          }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleMobileSelect(app);
          break;
        case 'Escape':
          e.preventDefault();
          setSearchText('');
          setStatusMessage('');
          inputRef.current?.focus();
          break;
        default:
          break;
      }
    },
    [handleMobileSelect],
  );

  const isAppPicked = Boolean(selectedApp?.link);
  const imgPath = '/img/logos/';

  const wrapperClassName = 'myOdu__searchAppsAutocompleteWrapper';

  const autocompleteClassName = [
    'myOdu__hideFloatLabel',
    'myOdu__searchAppsAutocomplete',
    isTouchLayout && 'mobile',
  ]
    .filter(Boolean)
    .join(' ');

  const isMobileResultsVisible =
    isActive && searchText.length > 0 && !selectedApp && filteredOptions.length > 0;
  const isMobileEmptyStateVisible =
    isActive && searchText.length > 0 && !selectedApp && filteredOptions.length === 0;

  const openAppButton = (
    <Button
      variant="outlined"
      size="small"
      disabled={!isAppPicked}
      onClick={onGoToApp}
      className="myOdu__button secondary myOdu__searchAppsAutocompleteButton"
      aria-label={
        isAppPicked ? `Open ${selectedApp?.name} in a new tab` : 'Select an application to open'
      }
    >
      Open App
    </Button>
  );

  const statusAndInstructions = (
    <>
      <div id={statusId} role="status" aria-live="polite" aria-atomic="true" class="sr-only">
        {statusMessage}
      </div>
      <div id={instructionsId} class="sr-only">
        {isTouchLayout
          ? 'Type your search, then tap Search on the keyboard to move to results. Double-tap a result to select it.'
          : 'Type to search, then use arrow keys to browse results.'}
      </div>
    </>
  );

  //touch requires diff layout because it doesnt handle the autocomplete drop down items
  //well. we have to render this more or less like a listbox and handle the kb nav
  if (isTouchLayout) {
    return (
      <Stack
        direction="column"
        spacing={1}
        className={wrapperClassName}
        role="search"
        aria-label="Application search"
        onFocus={handleWrapperFocus}
        onBlur={handleWrapperBlur}
      >
        <Stack direction="row" spacing={1} alignItems="stretch">
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            type="search"
            placeholder="Search applications"
            label="Search applications"
            className="myOdu__hideFloatLabel myOdu__searchAppsAutocomplete mobile"
            value={searchText}
            onChange={handleMobileInputChange}
            onKeyDown={handleMobileInputKeyDown}
            inputProps={{
              'aria-label': 'Search applications',
              'aria-describedby': instructionsId,
              'aria-expanded': isMobileResultsVisible,
              'aria-controls': isMobileResultsVisible ? resultsId : undefined,
              'aria-autocomplete': 'list',
              enterKeyHint: 'search',
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: 'false',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" aria-hidden="true">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputAdornment>
              ),
            }}
          />
          {openAppButton}
        </Stack>

        {isMobileResultsVisible && (
          <Paper
            elevation={3}
            component="ul"
            id={resultsId}
            role="listbox"
            aria-label="Matching applications"
            className="myOdu__mobileSearchResults"
          >
            {filteredOptions.map((option) => {
              const nameMatches = match(option.name, searchText, {
                insideWords: true,
              });
              const nameParts = parse(option.name, nameMatches);
              const descMatches = match(option.desc, searchText, {
                insideWords: true,
              });
              const descParts = parse(option.desc, descMatches);

              return (
                <li
                  key={option.id || option.name}
                  role="option"
                  tabIndex={0}
                  aria-selected={false}
                  onClick={() => handleMobileSelect(option)}
                  onKeyDown={(e) => handleOptionKeyDown(e, option)}
                  className="myOdu__mobileSearchOption"
                >
                  <Box
                    className="myOdu__logoZoom myOdu__searchAppsAutocompleteOptionLogo"
                    aria-hidden="true"
                  >
                    <img src={imgPath + option.icon} alt="" />
                  </Box>
                  <Stack className="myOdu__mobileSearchOptionText">
                    <Typography component="span">
                      {nameParts.map((part, i) => (
                        <span
                          key={i}
                          className={
                            part.highlight ? 'myOdu__searchAppsAutocompleteMatch' : undefined
                          }
                        >
                          {part.text}
                        </span>
                      ))}
                    </Typography>
                    <Typography
                      component="span"
                      variant="smaller"
                      className="myOdu__searchAppsAutocompleteOptionDesc"
                    >
                      {descParts.map((part, i) => (
                        <span
                          key={i}
                          className={
                            part.highlight ? 'myOdu__searchAppsAutocompleteMatch' : undefined
                          }
                        >
                          {part.text}
                        </span>
                      ))}
                    </Typography>
                  </Stack>
                </li>
              );
            })}
          </Paper>
        )}

        {isMobileEmptyStateVisible && (
          <Paper elevation={3} className="myOdu__mobileSearchResults">
            <Typography sx={{ p: 2 }}>No applications found</Typography>
          </Paper>
        )}

        {statusAndInstructions}
      </Stack>
    );
  }

  //desktop with active-descendant pattern (for SR)
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="stretch"
      className={wrapperClassName}
      role="search"
      aria-label="Application search"
      onFocus={handleWrapperFocus}
      onBlur={handleWrapperBlur}
    >
      <Autocomplete
        id={instanceId}
        className={autocompleteClassName}
        disablePortal
        selectOnFocus
        handleHomeEndKeys
        clearOnBlur={false}
        blurOnSelect={false}
        options={allApps}
        forcePopupIcon={false}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        inputValue={searchText}
        onInputChange={handleInputChange}
        value={selectedApp}
        onChange={handleAppChange}
        openOnFocus={false}
        noOptionsText="No applications found"
        ListboxProps={{
          'aria-label': 'Application suggestions',
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size="small"
            label="Search applications"
            placeholder="Search applications"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Search applications',
              'aria-describedby': instructionsId,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" aria-hidden="true">
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(optionProps, option, state) => {
          const { key, ...listItemProps } = optionProps;
          const nameMatches = match(option.name, state.inputValue, {
            insideWords: true,
          });
          const nameParts = parse(option.name, nameMatches);
          const descMatches = match(option.desc, state.inputValue, {
            insideWords: true,
          });
          const descParts = parse(option.desc, descMatches);

          return (
            <li key={key} {...listItemProps} aria-label={`${option.name} — ${option.desc}`}>
              <Box className="w-100" aria-hidden="true">
                <Stack direction="row" alignItems="center">
                  <Box className="myOdu__logoZoom myOdu__searchAppsAutocompleteOptionLogo">
                    <img src={imgPath + option.icon} alt="" />
                  </Box>
                  <Stack>
                    <Typography component="span">
                      {nameParts.map((part, index) => (
                        <span
                          key={index}
                          className={
                            part.highlight ? 'myOdu__searchAppsAutocompleteMatch' : undefined
                          }
                        >
                          {part.text}
                        </span>
                      ))}
                    </Typography>
                    <Typography
                      component="span"
                      variant="smaller"
                      className="myOdu__searchAppsAutocompleteOptionDesc"
                    >
                      {descParts.map((part, index) => (
                        <span
                          key={index}
                          className={
                            part.highlight ? 'myOdu__searchAppsAutocompleteMatch' : undefined
                          }
                        >
                          {part.text}
                        </span>
                      ))}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </li>
          );
        }}
      />

      {openAppButton}
      {statusAndInstructions}
    </Stack>
  );
}

export default SearchAppsAutocomplete;
